"use client";

import { useEffect, useRef } from "react";

interface UseStickyScrollOptions {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  /** Canonical short-id of the target version (`v7`); the hook queries
   *  `[data-scroll-target="version-${targetVersionShortId}"]` against the
   *  canonical attribute that `ChatVersionDivider` writes. */
  targetVersionShortId?: string | null;
  hasUnreadMessages?: boolean;
  messages: Array<{ id: string; createdAt: string }>;
  smoothScroll?: boolean;
  loadDependency?: unknown;
}

const SCROLL_OFFSET_TOP = 80;
const SCROLL_DURATION_MS = 650;
const SPRING_START = 0.82;
const SPRING_OSCILLATIONS = 2.2;
const SPRING_DAMPING = 6;
const SPRING_MAX_PX = 24;
const SPRING_MAX_RATIO = 0.04;

const easeOutQuint = (t: number) => 1 - Math.pow(1 - t, 5);
const nowMs = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

function clampScrollTop(container: HTMLDivElement, targetTop: number) {
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
  return Math.max(0, Math.min(targetTop, maxTop));
}

export function useStickyScroll({
  scrollRef,
  targetVersionShortId,
  hasUnreadMessages = false,
  messages,
  smoothScroll = true,
  loadDependency,
}: UseStickyScrollOptions) {
  const runIdRef = useRef(0);

  const messagesLength = messages.length;
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollToTop = (targetTop: number, instant: boolean) => {
      runIdRef.current += 1;
      const runId = runIdRef.current;
      const clampedTarget = clampScrollTop(container, targetTop);

      if (instant || !smoothScroll) {
        container.scrollTop = clampedTarget;
        return;
      }

      const startTop = container.scrollTop;
      const delta = clampedTarget - startTop;
      if (Math.abs(delta) < 1) return;

      const maxTop = Math.max(
        0,
        container.scrollHeight - container.clientHeight,
      );
      const overshootRoom = delta > 0 ? maxTop - clampedTarget : clampedTarget;
      const overshootLimit = Math.max(
        0,
        Math.min(
          SPRING_MAX_PX,
          Math.abs(delta) * SPRING_MAX_RATIO,
          overshootRoom,
        ),
      );
      const overshootDirection = delta >= 0 ? 1 : -1;
      const startTime = nowMs();

      const tick = (timestamp: number) => {
        if (runId !== runIdRef.current) return;
        if (!container.isConnected || scrollRef.current !== container) return;

        const elapsed = timestamp - startTime;
        const progress = Math.min(1, elapsed / SCROLL_DURATION_MS);

        if (progress < SPRING_START) {
          const eased = easeOutQuint(progress / SPRING_START);
          container.scrollTop = startTop + delta * eased;
        } else {
          const springT = (progress - SPRING_START) / (1 - SPRING_START);
          const omega = SPRING_OSCILLATIONS * Math.PI * 2;
          const springOffset =
            overshootLimit *
            Math.exp(-SPRING_DAMPING * springT) *
            Math.sin(omega * springT);
          const nextTop = clampedTarget + overshootDirection * springOffset;
          container.scrollTop = Math.max(0, Math.min(nextTop, maxTop));
        }

        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          container.scrollTop = clampScrollTop(container, clampedTarget);
        }
      };

      requestAnimationFrame(tick);
    };

    const scrollToElement = (element: HTMLElement) => {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const offsetTop = elementRect.top - containerRect.top;
      const targetTop = container.scrollTop + offsetTop - SCROLL_OFFSET_TOP;
      scrollToTop(targetTop, false);
    };

    const scrollToBottom = () => {
      scrollToTop(container.scrollHeight, false);
    };

    const run = () => {
      if (targetVersionShortId) {
        const versionEl = container.querySelector<HTMLElement>(
          `[data-scroll-target="version-${targetVersionShortId}"]`,
        );
        if (versionEl) {
          scrollToElement(versionEl);
          return;
        }
      }

      if (hasUnreadMessages) {
        const unreadEl = container.querySelector<HTMLElement>(
          '[data-scroll-target="unread"]',
        );
        if (unreadEl) {
          scrollToElement(unreadEl);
          return;
        }
      }

      scrollToBottom();
    };

    const rafId = requestAnimationFrame(run);
    return () => cancelAnimationFrame(rafId);
  }, [
    scrollRef,
    targetVersionShortId,
    hasUnreadMessages,
    messagesLength,
    smoothScroll,
    loadDependency,
  ]);
}
